export const VARIABLE_DEFINITIONS = [
  // Labor Rates
  { key: 'laborerRate', label: 'Laborer Rate', type: 'currency', defaultValue: 38.50, group: 'Labor Rates' },
  { key: 'irrigationRate', label: 'Irrigation / Supervisor Rate', type: 'currency', defaultValue: 95.50, group: 'Labor Rates' },
  { key: 'sprayTechRate', label: 'Spray Tech Rate', type: 'currency', defaultValue: 75.50, group: 'Labor Rates' },
  { key: 'operatorRate', label: 'Operator Rate', type: 'currency', defaultValue: 75.50, group: 'Labor Rates' },
  { key: 'emergencyRate', label: 'Emergency After-Hours Rate', type: 'currency', defaultValue: 143.25, group: 'Labor Rates' },

  // Specifications
  { key: 'treeCanopyHeight', label: 'Tree Canopy Height (ft)', type: 'number', defaultValue: 8, group: 'Specifications' },
  { key: 'mowingMinHeight', label: 'Mowing Min Height (in)', type: 'number', defaultValue: 1, group: 'Specifications' },
  { key: 'mowingMaxHeight', label: 'Mowing Max Height (in)', type: 'number', defaultValue: 2, group: 'Specifications' },
  { key: 'weedHeightThreshold', label: 'Weed Treatment Threshold (in)', type: 'number', defaultValue: 3, group: 'Specifications' },
  { key: 'ld50Threshold', label: 'LD 50 Threshold', type: 'number', defaultValue: 500, group: 'Specifications' },

  // Warranties & Periods
  { key: 'plantWarrantyDays', label: 'Plant Warranty (days)', type: 'number', defaultValue: 90, group: 'Warranties & Periods' },
  { key: 'irrigationWarrantyYears', label: 'Irrigation/Lighting Warranty (yrs)', type: 'number', defaultValue: 1, group: 'Warranties & Periods' },
  { key: 'lateChargePercent', label: 'Late Charge (%)', type: 'number', defaultValue: 2, group: 'Warranties & Periods' },
  { key: 'cancellationNoticeDays', label: 'Cancellation Notice (days)', type: 'number', defaultValue: 30, group: 'Warranties & Periods' },
  { key: 'proposalValidDays', label: 'Proposal Valid (days)', type: 'number', defaultValue: 60, group: 'Warranties & Periods' },
  { key: 'pricesGoodDays', label: 'Prices Good For (days)', type: 'number', defaultValue: 30, group: 'Warranties & Periods' },
  { key: 'equipmentMaxAge', label: 'Equipment Max Age (years)', type: 'number', defaultValue: 5, group: 'Warranties & Periods' },

  // Frequencies & Company
  { key: 'irrigationCheckFrequency', label: 'Irrigation Check Frequency', type: 'select', defaultValue: 'monthly', group: 'Frequencies & Company', options: [
    { value: 'weekly', label: 'Weekly', annualCount: 52 },
    { value: 'bi-weekly', label: 'Bi-Weekly', annualCount: 26 },
    { value: 'monthly', label: 'Monthly', annualCount: 12 },
    { value: 'bi-monthly', label: 'Every Other Month', annualCount: 6 },
    { value: 'quarterly', label: 'Quarterly', annualCount: 4 },
  ]},
  { key: 'preEmergentFrequency', label: 'Pre-Emergent Frequency', type: 'select', defaultValue: '2x', group: 'Frequencies & Company', options: [
    { value: '2x', label: '2x / Year (Jan & Jun)', annualCount: 2 },
    { value: '3x', label: '3x / Year', annualCount: 3 },
    { value: '4x', label: '4x / Year (Quarterly)', annualCount: 4 },
  ]},
  { key: 'companyName', label: 'Company Name', type: 'text', defaultValue: 'HMI', group: 'Frequencies & Company' },
  { key: 'entityNameFormal', label: 'Formal Entity Name', type: 'text', defaultValue: 'Handyman Maintenance, Inc.', group: 'Frequencies & Company' },
  { key: 'contractConditionsUrl', label: 'Contract Conditions URL', type: 'text', defaultValue: 'https://marianipremiergroup.com/contract-conditions', group: 'Frequencies & Company' },
  { key: 'privacyPolicyUrl', label: 'Privacy Policy URL', type: 'text', defaultValue: 'https://marianipremiergroup.com/privacy-policy', group: 'Frequencies & Company' },
  { key: 'holidayList', label: 'Holiday List', type: 'text', defaultValue: "New Year's Day, Memorial Day, July 4th, Labor Day, Thanksgiving, and Christmas", group: 'Frequencies & Company' },
  { key: 'pestExclusions', label: 'Pest Control Exclusions', type: 'text', defaultValue: 'bees and termites', group: 'Frequencies & Company' }
]

/**
 * Replace {{key}} placeholders in text with variable values.
 * Currency types get a '$' prefix.
 */
export function interpolate (text, variables) {
  if (!text || typeof text !== 'string') return text
  return text.replace(/\{\{(\w+)\}\}/g, (match, key) => {
    if (variables && variables[key] !== undefined) {
      const def = VARIABLE_DEFINITIONS.find(d => d.key === key)
      const val = variables[key]
      if (def && def.type === 'currency') {
        return '$' + Number(val).toFixed(2)
      }
      // For select types, show the display label instead of the raw value
      if (def && def.type === 'select' && def.options) {
        const opt = def.options.find(o => o.value === val)
        if (opt) return opt.label
      }
      return String(val)
    }
    return match
  })
}

/**
 * Merge code defaults + template variables + proposal variables.
 * Also computes derived values for select-type variables:
 *   {{key_annual}} = annualCount (e.g. 12 for monthly)
 *   {{key_label}} = display label (e.g. "Monthly")
 *   {{key_stat}} = stat-friendly string (e.g. "12x")
 */
export function resolveVariables (template, proposal) {
  const defaults = {}
  VARIABLE_DEFINITIONS.forEach(d => { defaults[d.key] = d.defaultValue })
  const merged = {
    ...defaults,
    ...(template?.variables || {}),
    ...(proposal?.variables || {})
  }

  // Compute derived values for select-type variables
  VARIABLE_DEFINITIONS.forEach(d => {
    if (d.type === 'select' && d.options) {
      const selectedValue = merged[d.key]
      const opt = d.options.find(o => o.value === selectedValue) || d.options[0]
      if (opt) {
        merged[d.key + '_annual'] = opt.annualCount
        merged[d.key + '_label'] = opt.label
        merged[d.key + '_stat'] = opt.annualCount + 'x'
      }
    }
  })

  return merged
}
